// src/lib/rppg.ts

import FFT from 'fft.js';
import { RPPG_CONFIG } from '../config/constants';
import type { RppgResult } from '../types/triage';

export interface ROI {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function extractROI(videoWidth: number, videoHeight: number): ROI {
  const { X_CENTER, Y_CENTER, WIDTH, HEIGHT } = RPPG_CONFIG.ROI;

  return {
    x: Math.floor(videoWidth * (X_CENTER - WIDTH / 2)),
    y: Math.floor(videoHeight * (Y_CENTER - HEIGHT / 2)),
    width: Math.floor(videoWidth * WIDTH),
    height: Math.floor(videoHeight * HEIGHT),
  };
}

export function extractMeanGreenValue(
  ctx: CanvasRenderingContext2D,
  roi: ROI
): number {
  const imageData = ctx.getImageData(roi.x, roi.y, roi.width, roi.height);
  const data = imageData.data;

  let gSum = 0;
  const pixelCount = roi.width * roi.height;

  for (let i = 0; i < data.length; i += 4) {
    gSum += data[i + 1]; // Green channel
  }

  return gSum / pixelCount;
}

export function detrend(signal: number[]): number[] {
  const mean = signal.reduce((a, b) => a + b, 0) / signal.length;
  return signal.map(v => v - mean);
}

export function applyHanningWindow(signal: number[]): number[] {
  const N = signal.length;
  return signal.map((v, i) => {
    const window = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N - 1)));
    return v * window;
  });
}

export function nextPowerOfTwo(n: number): number {
  return Math.pow(2, Math.ceil(Math.log2(n)));
}

export function computeFFT(signal: number[]): Float32Array {
  const fftSize = nextPowerOfTwo(signal.length);
  const fft = new FFT(fftSize);

  // Pad signal to power of 2
  const paddedSignal = new Array(fftSize).fill(0);
  for (let i = 0; i < signal.length; i++) {
    paddedSignal[i] = signal[i];
  }

  const out = fft.createComplexArray();
  fft.realTransform(out, paddedSignal);

  // Calculate magnitude
  const magnitude = new Float32Array(fftSize / 2);
  for (let i = 0; i < fftSize / 2; i++) {
    const real = out[2 * i];
    const imag = out[2 * i + 1];
    magnitude[i] = Math.sqrt(real * real + imag * imag);
  }

  return magnitude;
}

export function findPeakInRange(
  spectrum: Float32Array,
  freqRange: readonly [number, number],
  fps: number
): { frequency: number; power: number } | null {
  const [minFreq, maxFreq] = freqRange;
  const N = spectrum.length * 2; // Original FFT size

  const minIdx = Math.floor((minFreq * N) / fps);
  const maxIdx = Math.ceil((maxFreq * N) / fps);

  let peakIdx = -1;
  let peakPower = 0;

  for (let i = minIdx; i <= maxIdx && i < spectrum.length; i++) {
    if (spectrum[i] > peakPower) {
      peakPower = spectrum[i];
      peakIdx = i;
    }
  }

  if (peakIdx === -1) {
    return null;
  }

  const frequency = (peakIdx * fps) / N;
  return { frequency, power: peakPower };
}

function computeConfidence(
  spectrum: Float32Array,
  peakPower: number
): number {
  // Calculate total power
  let totalPower = 0;
  for (let i = 0; i < spectrum.length; i++) {
    totalPower += spectrum[i] * spectrum[i];
  }

  if (totalPower === 0) return 0;

  // Confidence = peak power ratio
  const confidence = (peakPower * peakPower) / totalPower;
  return Math.min(1, confidence * 10); // Scale up for better discrimination
}

export function processRppgSignal(
  gSignal: number[],
  fps: number = 30
): RppgResult {
  console.log('🔬 rPPG Debug - Signal length:', gSignal.length, 'samples');
  console.log('🔬 rPPG Debug - FPS:', fps);
  console.log('🔬 rPPG Debug - Duration:', (gSignal.length / fps).toFixed(2), 'seconds');

  if (gSignal.length < 30) {
    console.warn('❌ Not enough samples for rPPG');
    return {
      hr_bpm: null,
      rr_bpm: null,
      hr_confidence: null,
      rr_confidence: null,
    };
  }

  try {
    // Check signal quality
    const mean = gSignal.reduce((a, b) => a + b, 0) / gSignal.length;
    const variance = gSignal.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / gSignal.length;
    const stdDev = Math.sqrt(variance);
    console.log('🔬 Signal stats - Mean:', mean.toFixed(2), 'StdDev:', stdDev.toFixed(2));
    console.log('🔬 First 10 samples:', gSignal.slice(0, 10).map(v => v.toFixed(2)).join(', '));
    console.log('🔬 Last 10 samples:', gSignal.slice(-10).map(v => v.toFixed(2)).join(', '));

    // 1. Detrend
    const detrended = detrend(gSignal);
    console.log('🔬 After detrend - Range:', Math.min(...detrended).toFixed(2), 'to', Math.max(...detrended).toFixed(2));

    // 2. Apply windowing
    const windowed = applyHanningWindow(detrended);

    // 3. Compute FFT
    const spectrum = computeFFT(windowed);
    console.log('🔬 FFT spectrum length:', spectrum.length);

    // Spectrum analysis
    const totalSpectralPower = spectrum.reduce((sum, val) => sum + val * val, 0);
    const maxSpectralPower = Math.max(...Array.from(spectrum));
    console.log('🔬 Total spectral power:', totalSpectralPower.toFixed(2));
    console.log('🔬 Max spectral power:', maxSpectralPower.toFixed(2));

    // 4. Find HR peak
    const hrPeak = findPeakInRange(spectrum, RPPG_CONFIG.HR_FREQ_RANGE, fps);
    console.log('🔬 HR Peak:', hrPeak ? `${hrPeak.frequency.toFixed(3)} Hz (${(hrPeak.frequency * 60).toFixed(1)} bpm), power: ${hrPeak.power.toFixed(2)}` : 'None');

    const hr_bpm = hrPeak ? hrPeak.frequency * 60 : null;
    const hr_confidence = hrPeak ? computeConfidence(spectrum, hrPeak.power) : null;
    console.log('🔬 HR Confidence:', hr_confidence ? (hr_confidence * 100).toFixed(1) + '%' : 'N/A');

    // 5. Find RR peak
    const rrPeak = findPeakInRange(spectrum, RPPG_CONFIG.RR_FREQ_RANGE, fps);
    console.log('🔬 RR Peak:', rrPeak ? `${rrPeak.frequency.toFixed(3)} Hz (${(rrPeak.frequency * 60).toFixed(1)} bpm), power: ${rrPeak.power.toFixed(2)}` : 'None');

    const rr_bpm = rrPeak ? rrPeak.frequency * 60 : null;
    const rr_confidence = rrPeak ? computeConfidence(spectrum, rrPeak.power) : null;
    console.log('🔬 RR Confidence:', rr_confidence ? (rr_confidence * 100).toFixed(1) + '%' : 'N/A');

    console.log('🔬 Min confidence threshold:', RPPG_CONFIG.MIN_CONFIDENCE);

    // Return null if confidence too low
    return {
      hr_bpm: hr_confidence && hr_confidence >= RPPG_CONFIG.MIN_CONFIDENCE ? hr_bpm : null,
      rr_bpm: rr_confidence && rr_confidence >= RPPG_CONFIG.MIN_CONFIDENCE ? rr_bpm : null,
      hr_confidence,
      rr_confidence,
    };
  } catch (error) {
    console.error('rPPG processing error:', error);
    return {
      hr_bpm: null,
      rr_bpm: null,
      hr_confidence: null,
      rr_confidence: null,
    };
  }
}
